import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Divider, Rating, Avatar, CircularProgress, Alert
} from '@mui/material';
import dayjs from 'dayjs';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const MyReviews = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMyReviews = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const user = JSON.parse(localStorage.getItem('user'));

                if (!user || !user.role || !user.id) {
                    throw new Error('User information not found.');
                }

                const response = await axios.get(`${API_BASE_URL}/api/reviews/${user.role}/${user.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                setReviews(response.data.reviews);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to fetch reviews.');
            } finally {
                setLoading(false);
            }
        };

        fetchMyReviews();
    }, []);

    const reviewSummary = useMemo(() => {
        if (reviews.length === 0) return { avg: 0, count: 0 };
        const total = reviews.reduce((acc, r) => acc + r.rating, 0);
        return {
            avg: total / reviews.length,
            count: reviews.length
        };
    }, [reviews]);

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                My Reviews ({reviewSummary?.count})
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Rating value={reviewSummary.avg} precision={0.5} readOnly />
                <Typography variant="body1" sx={{ ml: 1, fontWeight: 600 }}>
                    {reviewSummary.avg.toFixed(1)} / 5
                </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ 
                maxHeight: '60vh', 
                overflowY: 'auto', 
                pr: 2,
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#555',
                },
            }}>
                {reviews.length > 0 ? (
                    reviews.map((review) => (
                        <Paper key={review.id} elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, border: "1px solid #e6e6e6", background: "#ffffff" }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: "#1976d2", mr: 1, fontSize: "0.9rem" }}>
                                    {review.reviewer_company_name?.charAt(0)}
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                        {review.reviewer_company_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {dayjs(review.created_at).format("MM/DD/YYYY")}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Rating value={review.rating} readOnly size="small" />
                                <Typography variant="caption" sx={{ ml: 1, fontWeight: 600 }}>
                                    {review.rating}/5
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ background: "#f8f9fa", p: 1, borderRadius: 1.5, lineHeight: 1.4, border: "1px solid #eee" }}>
                                {review.comment}
                            </Typography>
                        </Paper>
                    ))
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        You have no approved reviews yet.
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default MyReviews;